import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";

/**
 * SiteHeader — global header used across the Pivotbox site.
 * Desktop: inline tabs (Home · Full Diagnostic · Contact Us).
 * Mobile (<= 768px): logo + hamburger; tapping it opens a slide-in drawer
 * with Home · Pivotbox Framework · Take the Founder Signal Test - Free ·
 * Full Diagnostic · Contact Us.
 *
 * "Full Diagnostic" tab auto-hides on the /find-out page (it IS that page).
 *
 * Uses the existing .fs-header* classes (already loaded in CSS) so the visual
 * styling is identical to FiveSFrameworkPage / SDiagBase.
 */
export default function SiteHeader({ testIdPrefix = "site" }) {
  const location = useLocation();
  const contactHref = `${location.pathname}${location.search}#site-footer`;
  const isFindOut = location.pathname === "/find-out" || location.pathname === "/find-out/";
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileNavOpen]);

  const closeNav = () => setMobileNavOpen(false);

  return (
    <>
      <header className="fs-header" data-testid={`${testIdPrefix}-header`}>
        <Link to="/" data-testid={`${testIdPrefix}-header-logo`} onClick={closeNav}>
          <img src="/pivotbox-p-logo.png" alt="Pivotbox" className="fs-header__logo" />
        </Link>
        <nav className="fs-header__nav" data-testid={`${testIdPrefix}-header-nav`}>
          <Link to="/" className="fs-header__nav-link" data-testid={`${testIdPrefix}-nav-home`}>
            Home
          </Link>
          {!isFindOut && (
            <Link to="/find-out" className="fs-header__nav-link" data-testid={`${testIdPrefix}-nav-full-diagnostic`}>
              Full Diagnostic
            </Link>
          )}
          <a href={contactHref} className="fs-header__nav-link fs-header__nav-link--cta" data-testid={`${testIdPrefix}-nav-contact`}>
            Contact Us
          </a>
        </nav>

        {/* Mobile hamburger */}
        <button
          type="button"
          className="site-nav__toggle site-nav__toggle--solid"
          aria-label="Open menu"
          aria-expanded={mobileNavOpen}
          aria-controls={`${testIdPrefix}-mobile-nav-drawer`}
          onClick={() => setMobileNavOpen(true)}
          data-testid={`${testIdPrefix}-nav-mobile-toggle`}
        >
          <Menu size={22} aria-hidden="true" />
          <span className="site-nav__toggle-dot" aria-hidden="true" />
        </button>
      </header>

      {/* Mobile drawer */}
      <div
        id={`${testIdPrefix}-mobile-nav-drawer`}
        className={`mobile-nav ${mobileNavOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!mobileNavOpen}
        data-testid={`${testIdPrefix}-mobile-nav-drawer`}
      >
        <button
          type="button"
          className="mobile-nav__backdrop"
          aria-label="Close menu"
          onClick={closeNav}
          tabIndex={mobileNavOpen ? 0 : -1}
          data-testid={`${testIdPrefix}-mobile-nav-backdrop`}
        />
        <div className="mobile-nav__panel" data-testid={`${testIdPrefix}-mobile-nav-panel`}>
          <div className="mobile-nav__header">
            <span className="mobile-nav__title">Menu</span>
            <button
              type="button"
              className="mobile-nav__close"
              aria-label="Close menu"
              onClick={closeNav}
              data-testid={`${testIdPrefix}-mobile-nav-close`}
            >
              <X size={22} aria-hidden="true" />
            </button>
          </div>
          <nav className="mobile-nav__links" data-testid={`${testIdPrefix}-mobile-nav-links`}>
            <Link
              to="/"
              className="mobile-nav__link"
              onClick={closeNav}
              data-testid={`${testIdPrefix}-mobile-nav-link-home`}
            >
              <span>Home</span>
              <ArrowRight size={16} aria-hidden="true" className="mobile-nav__chev" />
            </Link>

            <Link
              to="/#framework"
              className="mobile-nav__link"
              onClick={closeNav}
              data-testid={`${testIdPrefix}-mobile-nav-link-framework`}
            >
              <span>Pivotbox Framework</span>
              <ArrowRight size={16} aria-hidden="true" className="mobile-nav__chev" />
            </Link>

            <Link
              to="/foundersignaltest"
              className="mobile-nav__link mobile-nav__link--cta"
              onClick={closeNav}
              data-testid={`${testIdPrefix}-mobile-nav-link-founder-signal-test`}
            >
              <span>Take the Founder Signal Test — Free</span>
              <ArrowRight size={16} aria-hidden="true" className="mobile-nav__chev" />
            </Link>

            {!isFindOut && (
              <Link
                to="/find-out"
                className="mobile-nav__link"
                onClick={closeNav}
                data-testid={`${testIdPrefix}-mobile-nav-link-full-diagnostic`}
              >
                <span>Full Diagnostic</span>
                <ArrowRight size={16} aria-hidden="true" className="mobile-nav__chev" />
              </Link>
            )}

            <a
              href={contactHref}
              className="mobile-nav__link"
              onClick={closeNav}
              data-testid={`${testIdPrefix}-mobile-nav-link-contact`}
            >
              <span>Contact Us</span>
              <ArrowRight size={16} aria-hidden="true" className="mobile-nav__chev" />
            </a>
          </nav>
        </div>
      </div>
    </>
  );
}
